import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FileText, Receipt, Bell, FileWarning, FileCheck, type LucideIcon } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { DataTable } from '@/components/DataTable'
import { AsyncBoundary } from '@/components/AsyncBoundary'

type DocumentType = 'receipt' | 'invoice' | 'statement' | 'notification' | 'other'

interface FinanceDocumentRow {
  id: string
  fromAddress: string
  fromName: string | null
  subject: string
  receivedAt: string
  amount: string | null
  currency: string | null
  documentType: DocumentType
  attachmentUrls: string[]
  emailMessage: {
    id: string
    subject: string
    fromName: string | null
    fromEmail: string
  } | null
}

interface Summary {
  total: number
  byType: { documentType: DocumentType; count: number }[]
}

const TYPE_ICON: Record<DocumentType, LucideIcon> = {
  receipt: Receipt,
  invoice: FileWarning,
  statement: FileCheck,
  notification: Bell,
  other: FileText,
}

const TYPE_LABEL: Record<DocumentType, string> = {
  receipt: 'Receipt',
  invoice: 'Invoice',
  statement: 'Statement',
  notification: 'Notification',
  other: 'Other',
}

const TYPE_COLOUR: Record<DocumentType, string> = {
  receipt: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  invoice: 'bg-amber-50 text-amber-900 border-amber-200',
  statement: 'bg-sky-50 text-sky-800 border-sky-200',
  notification: 'bg-slate-50 text-slate-700 border-slate-200',
  other: 'bg-ovation-ink/5 text-ovation-ink border-ovation-ink/15',
}

export function FinanceDocumentsPage() {
  const [filter, setFilter] = useState<DocumentType | 'all'>('all')

  const summaryQ = useQuery<Summary, ApiClientError>({
    queryKey: ['finance-summary'],
    queryFn: () => artistFetch<Summary>('/finance/documents/summary'),
  })
  const docsQ = useQuery<FinanceDocumentRow[], ApiClientError>({
    queryKey: ['finance-documents', filter],
    queryFn: () => {
      const qs = filter === 'all' ? '' : `?documentType=${filter}`
      return artistFetch<FinanceDocumentRow[]>(`/finance/documents${qs}`)
    },
  })

  return (
    <div>
      <PageHeader
        title="Finance documents"
        subtitle="Receipts, invoices, statements, and notifications auto-ingested from inbound email. No human is emailed — the Finance team reviews here."
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <FilterChip
          active={filter === 'all'}
          onClick={() => setFilter('all')}
          label="All"
          count={summaryQ.data?.total}
        />
        {(['receipt', 'invoice', 'statement', 'notification', 'other'] as const).map((t) => {
          const count = summaryQ.data?.byType.find((b) => b.documentType === t)?.count ?? 0
          return (
            <FilterChip
              key={t}
              active={filter === t}
              onClick={() => setFilter(t)}
              label={TYPE_LABEL[t]}
              count={count}
            />
          )
        })}
      </div>

      <AsyncBoundary isLoading={docsQ.isLoading} error={docsQ.error}>
        <DataTable
          rows={docsQ.data ?? []}
          rowKey={(r) => r.id}
          emptyMessage="No finance documents yet. Inbound automated emails (receipts, invoices, etc.) will appear here."
          columns={[
            {
              key: 'type',
              header: 'Type',
              render: (r) => {
                const Icon = TYPE_ICON[r.documentType] ?? FileText
                return (
                  <span
                    className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs ${TYPE_COLOUR[r.documentType]}`}
                  >
                    <Icon className="h-3 w-3" />
                    {TYPE_LABEL[r.documentType]}
                  </span>
                )
              },
            },
            {
              key: 'from',
              header: 'From',
              render: (r) => (
                <div>
                  <div className="font-medium">{r.fromName || r.fromAddress}</div>
                  {r.fromName && (
                    <div className="text-xs text-ovation-muted">{r.fromAddress}</div>
                  )}
                </div>
              ),
            },
            { key: 'subject', header: 'Subject', render: (r) => r.subject },
            {
              key: 'amount',
              header: 'Amount',
              align: 'right',
              render: (r) =>
                r.amount
                  ? `${r.currency ?? ''} ${Number(r.amount).toLocaleString()}`
                  : <span className="text-xs text-ovation-muted">—</span>,
            },
            {
              key: 'received',
              header: 'Received',
              render: (r) => new Date(r.receivedAt).toLocaleString(),
            },
            {
              key: 'attachments',
              header: '',
              align: 'right',
              render: (r) =>
                r.attachmentUrls.length > 0 ? (
                  <span className="text-xs text-ovation-muted">
                    {r.attachmentUrls.length} file{r.attachmentUrls.length === 1 ? '' : 's'}
                  </span>
                ) : null,
            },
          ]}
        />
      </AsyncBoundary>
    </div>
  )
}

function FilterChip({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean
  onClick: () => void
  label: string
  count: number | undefined
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm ${
        active
          ? 'border-ovation-ink bg-ovation-ink text-ovation-paper'
          : 'border-ovation-ink/15 bg-white text-ovation-ink hover:bg-ovation-ink/5'
      }`}
    >
      <span>{label}</span>
      {count != null && (
        <span
          className={`rounded-full px-1.5 py-0.5 text-[10px] ${
            active ? 'bg-white/20' : 'bg-ovation-ink/5'
          }`}
        >
          {count}
        </span>
      )}
    </button>
  )
}
